import api from "@/services/api.ts";
import {
    Mentor,
    UpdateMentorMetaDto,
    UpdateMentorAvailabilityDto,
    UpdateMentorDetailsDto,
    MentorWithMatchedMentees
} from "@/types/Mentor.ts";

const URL = '/mentors';

export const updateMentorWithMetadata = async (id: number, mentor: Partial<UpdateMentorMetaDto>): Promise<Mentor> => {
    try {
        const { data } = await api.put(`${URL}/${id}`, mentor);
        return data;
    } catch (error) {
        throw new Error('Failed to update mentor.');
    }
};

export const updateMentorWithAvailability = async (id: number, mentor: UpdateMentorAvailabilityDto): Promise<Mentor> => {
    try {
        const { data } = await api.put(`${URL}/${id}`, mentor);
        return data;
    } catch (error) {
        throw new Error('Failed to update mentor.');
    }
};

export const updateMentorWithDetails = async (id: number, mentor: UpdateMentorDetailsDto): Promise<Mentor> => {
    try {
        const { data } = await api.put(`${URL}/${id}`, mentor);
        return data;
    } catch (error) {
        throw new Error('Failed to update mentor.');
    }

};

export const getMentors = async (key = "", seasonId?: number): Promise<Mentor[]> => {
    const separator = key.includes("?") ? "&" : "?";
    const seasonParam = seasonId ? `${separator}seasonId=${seasonId}` : "";
    const { data } = await api.get<Mentor[]>(`${URL}${key}${seasonParam}`);
    return data;
};

export const getMentorById = async (id: number): Promise<Mentor | null> => {
    try {
        const response = await api.get(`${URL}/${id}`);
        return response.data;
    } catch (error: any) {
        if (error.response && error.response.status === 404) {
            return null;
        }
        throw error;
    }
};

export const getMentorsWithMatchedMentees = async (): Promise<MentorWithMatchedMentees[]> => {
    const response = await api.get<MentorWithMatchedMentees[]>(`${URL}/matched-mentees`);
    return response.data;
};

export type MentorSessionForList = {
    sessionId: number;
    sessionDate: string;
    sessionTime: string;
    hasMentorNote: boolean;
    hasMenteeFeedback: boolean;
};
export type SessionStatus = "attended" | "missed" | "upcoming";
export type MenteeWithSessionStatuses = {
    id: number;
    user: { fullName: string; email: string; profilePictureSrc: string | null };
    sessionStatuses: { sessionId: number; status: SessionStatus; feedbackSubmitted: boolean }[];
};

export type MentorMenteesWithSessionsResponse = {
    sessions: MentorSessionForList[];
    mentees: MenteeWithSessionStatuses[];
};

export const getMentorMenteesWithSessions = async (mentorId: number): Promise<MentorMenteesWithSessionsResponse> => {
    const { data } = await api.get<MentorMenteesWithSessionsResponse>(`${URL}/${mentorId}/mentees-with-sessions`);
    return data;
};

export type MentorProfileCompletionStats = {
    completedCount: number;
    totalMentors: number;
    percentChangeToday: number;
    timeSeries: { day: string; total: number }[];
};

/** Fetches mentor profile completion stats for the dashboard. */
export const getMentorProfileCompletionStats = async (): Promise<MentorProfileCompletionStats> => {
    const { data } = await api.get<MentorProfileCompletionStats>(`${URL}/profile-completion-stats`);
    return data;
};

export type MentorBreakdownBucket = { label: string; count: number; prevCount?: number };

export type MentorBreakdownStats = {
    byDiscipline: MentorBreakdownBucket[];
    byResearchExpertise: MentorBreakdownBucket[];
    byExperience: MentorBreakdownBucket[];
    totalCurrent: number;
    totalPrev?: number;
};

export const getMentorBreakdownStats = async (seasonId?: number): Promise<MentorBreakdownStats> => {
    const suffix = seasonId ? `?seasonId=${seasonId}` : "";
    const { data } = await api.get<MentorBreakdownStats>(`${URL}/breakdown${suffix}`);
    return data;
};
